import type {CSSProperties, MutableRefObject} from 'react';
import React, {useEffect, useRef} from 'react';
import type {GridItemStorage} from '@Components/base-grid/components//grid-item';
import {toggleGridItemSelection, updateMaxSelectionNumberForGrid} from '@Components/base-grid/base-grid-reducer';
import {getGridItemForStoredData} from '@Components/base-grid/base-grid';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './base-standard-grid.module.scss';

const DEFAULT_GRID_ITEM_MAX_WIDTH = 200;

/**
 * Vertical offset to check during lazy loading whether the user has scrolled all the way down minus a vertical offset to kickstart lazyload
 * @type {number}
 */
const BOTTOM_SCROLL_OFFSET = 100;

export interface BaseStandardGridProps {
  /**
   * The id associated with the grid which keeps track of all the items.
   */
  gridId: string;
  /**
   * The id of selection group this grid is part of.
   *
   * This is used when you want to keep track of selected items accross multiple grids. For that case you would specify the same
   * gridSelectionGroupId for all these grids.
   *
   * If not specified, defaults to the group id hence creating a unique selection group for the grid.
   */
  gridSelectionGroupId?: string;
  gridItemAspectRatio?: number;
  gridItemMaxWidth?: number;
  className?: string;
  scrollingContainer?: HTMLDivElement | null;
  gridTitleClasses?: string;
  gridStyle?: CSSProperties;
  gridContainerStyle?: CSSProperties;

  attachScrollListener?(cb: () => void): void;

  gridTitle?: string;

  /**
   * If specified the default handler that selects/unselects an item doesn't fire. Instead this is called
   */
  customOnGridItemClicked?(item: GridItemStorage, isDoubleClick: boolean): void;

  /**
   * 0 value for it means no limit on amount of selected items
   */
  maxSelectionNumber?: number;
  excludeItemWithExtension?: string;

  onScrolledToBottom?(): void;

  makeContentScrollable?: boolean;

  gridRef?(ref: HTMLDivElement | null): void;

  gridClassName?: string;
  doSetGridInlineStyles?: boolean;
}

/**
 * codeReviewHamza: we can launch with this if this is important since this branch also has many important React use updates, lets discuss if we want to re-visit the architecture here and revamp after launching this.
 */
export function BaseStandardGrid({
  className = '',
  onScrolledToBottom = $.noop,
  attachScrollListener = $.noop,
  gridItemAspectRatio = 1,
  gridItemMaxWidth = DEFAULT_GRID_ITEM_MAX_WIDTH,
  excludeItemWithExtension = '',
  scrollingContainer = null,
  gridTitleClasses = '',
  gridStyle = {},
  gridContainerStyle = {},
  gridTitle = '',
  gridRef = $.noop,
  makeContentScrollable = true,
  gridClassName = '',
  doSetGridInlineStyles = true,
  ...props
}: BaseStandardGridProps) {
  const selectionGroupId = props.gridSelectionGroupId ?? props.gridId;
  const gridContainer: MutableRefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
  const gridEl = useRef<HTMLUListElement>(null);
  const dispatch = useAppDispatch();
  const gridData = useAppSelector((state) => {
    return state.grids.gridsHashmap[props.gridId];
  });

  useEffect(() => {
    attachScrollToBottom();

    return (): void => {
      if (gridContainer.current) {
        const gridContainerJQuery = $(gridContainer.current);
        gridContainerJQuery.off('scroll');
      }
      if (scrollingContainer) {
        const scrollingContainerJQuery = $(scrollingContainer);
        scrollingContainerJQuery.off('scroll');
      }
    };
  });

  useEffect(() => {
    dispatch(
      updateMaxSelectionNumberForGrid({
        gridSelectionGroupId: selectionGroupId,
        maxSelectionNumber: props.maxSelectionNumber ?? 0,
      })
    );
  }, [props.maxSelectionNumber]);

  const attachScrollToBottom = () => {
    if (typeof attachScrollListener === 'function' && attachScrollListener !== $.noop) {
      return attachScrollListener(onScrolledToBottom);
    }

    const gridScrollingContainer = getScrollingContainer();
    if (gridScrollingContainer) {
      const gridScrollingContainerJQuery = $(gridScrollingContainer);

      gridScrollingContainerJQuery.on(
        'scroll',
        window.PMW.debounce(() => {
          if (hasGridScrolledToBottom()) {
            onScrolledToBottom();
          }
        }, 100)
      );
    }
  };

  const getScrollingContainer = () => {
    if (scrollingContainer) {
      return scrollingContainer;
    }
    return gridContainer.current;
  };

  const hasGridScrolledToBottom = () => {
    const gridScrollingContainer = getScrollingContainer();
    if (gridScrollingContainer && gridEl.current) {
      const gridScrollingContainerJQuery = $(gridScrollingContainer);
      const gridElJQuery = $(gridEl.current);
      const gridHeight = gridElJQuery.height();
      const gridScrollingContainerHeight = gridScrollingContainerJQuery.height();
      const gridContainerScrollPosition = gridScrollingContainerJQuery.scrollTop();

      if (gridHeight === undefined || gridScrollingContainerHeight === undefined || gridContainerScrollPosition === undefined) {
        return false;
      }

      if (gridHeight >= gridScrollingContainerHeight && gridHeight - gridScrollingContainerHeight - gridContainerScrollPosition <= BOTTOM_SCROLL_OFFSET) {
        return true;
      }
    }
    return false;
  };

  const isExtensionAllowed = (ext: string): boolean => {
    if (!excludeItemWithExtension) {
      return true;
    }

    return ext !== excludeItemWithExtension;
  };

  const getCssStylesForGridItem = (): CSSProperties => {
    const gridItemMinWidth = gridItemMaxWidth * 0.85;
    const gridItemMinHeight = gridItemMinWidth / gridItemAspectRatio;
    const gridItemMaxHeight = gridItemMaxWidth / gridItemAspectRatio;

    return {
      maxWidth: `${gridItemMaxWidth}px`,
      maxHeight: `${gridItemMaxHeight}px`,
      minWidth: `${gridItemMinWidth}px`,
      minHeight: `${gridItemMinHeight}px`,
      width: 'initial',
      height: 'initial',
      aspectRatio: gridItemAspectRatio.toString(),
    };
  };

  /**
   * codeReviewHamza: lets discuss this. I am not in favor of switch case like this in the getGridItemForStoredData
   * so what you are doing below, React.CloneElement is good, and in fact it is needed to attach custom handlers for selection, that's fine. but we can improve get grid item wala part.
   * Modify and prepare grid items before adding them to grid
   * @return {*[]}
   */
  const prepGridItems = () => {
    const gridItems = [];
    if (!gridData) {
      return [];
    }
    for (const item of gridData.items) {
      if (!item.extension || isExtensionAllowed(item.extension)) {
        gridItems.push(
          getGridItemForStoredData({
            item,
            paddedThumb: true,
            gridId: props.gridId,
            gridSelectionGroupId: selectionGroupId,
            className: `${styles.gridItem} ${item.className ?? ''}`,
            showSelectionNumber: props?.maxSelectionNumber !== 1,
            onClicked: onGridItemClicked.bind(null, item),
            style: getCssStylesForGridItem(),
          })
        );
      }
    }

    return gridItems;
  };

  const onGridItemClicked = (item: GridItemStorage, e: React.MouseEvent, isDoubleClick: boolean): void => {
    if (props.customOnGridItemClicked !== undefined) {
      props.customOnGridItemClicked(item, isDoubleClick);
      return;
    }
    dispatch(
      toggleGridItemSelection({
        gridItemId: item.id,
        gridSelectionGroupId: selectionGroupId,
      })
    );
  };

  const getGridStyles = () => {
    if (!doSetGridInlineStyles) {
      return undefined;
    }

    const minGridItemWidth = gridItemMaxWidth * 0.85;
    return {
      gridTemplateColumns: `repeat( auto-fill, minmax(${minGridItemWidth}px, 1fr))`,
      ...gridStyle,
    };
  };

  return (
    <>
      <div className={`grid-title body-l-bold ${gridTitleClasses}`}>{gridTitle}</div>
      <div
        style={gridContainerStyle}
        className={`${styles.multiMediaGridContainerReact} ${className}  ${makeContentScrollable ? styles.multiMediaScrollContainer : ''} `}
        ref={(ref) => {
          gridContainer.current = ref;
          gridRef(ref);
        }}
      >
        <ul className={`${styles.gridReact} ${gridClassName} grid-react`} ref={gridEl} style={getGridStyles()}>
          {prepGridItems()}
        </ul>
      </div>
    </>
  );
}
